import { SentimentPolarity } from '@up2stay/up2promo-api-client';

import type {
  ReviewsFilterValues,
  ReviewsInboxTab,
} from '@/core/components/reputation/reviews/reviews-filters';

const SENTIMENT_FILTER_VALUES: SentimentPolarity[] = [
  SentimentPolarity.POSITIVE,
  SentimentPolarity.NEUTRAL,
  SentimentPolarity.NEGATIVE,
];

export type ReviewsInboxFocusState = {
  focusReviewId?: string;
  openReply?: boolean;
};

export function parseReviewFilters(search: string) {
  const params = new URLSearchParams(search);
  return {
    locationId: params.get('locationId') ?? undefined,
    reviewId: params.get('reviewId')?.trim() || undefined,
    openReply: params.get('reply') === '1',
    sentiment: params.get('sentiment') ?? undefined,
    aspect: params.get('aspect') ?? undefined,
    responded: params.get('responded') ?? undefined,
  };
}

export function getInboxFocus(location: { search: string; state: unknown }): {
  reviewId?: string;
  openReply: boolean;
} {
  const state = location.state as ReviewsInboxFocusState | null | undefined;
  const fromUrl = parseReviewFilters(location.search);
  return {
    reviewId: state?.focusReviewId ?? fromUrl.reviewId,
    openReply: Boolean(state?.openReply ?? fromUrl.openReply),
  };
}

export function clearInboxFocusFromNavigation(
  pathname: string,
  search: string,
) {
  const params = new URLSearchParams(search);
  params.delete('reviewId');
  params.delete('reply');
  const nextSearch = params.toString();
  return {
    pathname,
    search: nextSearch ? `?${nextSearch}` : '',
  };
}

function isSentimentFilter(value: string): value is SentimentPolarity {
  return SENTIMENT_FILTER_VALUES.includes(value as SentimentPolarity);
}

/**
 * Apply drilldown query params (?aspect=, ?sentiment=, ?responded=, …) to inbox
 * filters. Without an explicit `responded`, a topical drilldown lists every
 * review (the aspect is the point); a bare visit starts on the open tab.
 */
export function drilldownStateFromSearch(
  search: string,
  focusReviewId?: string,
): Pick<ReviewsFilterValues, 'tab' | 'locationIds' | 'aspects' | 'sentiments'> {
  const f = parseReviewFilters(search);
  const sentiments =
    f.sentiment && isSentimentFilter(f.sentiment) ? [f.sentiment] : [];
  const aspects = f.aspect ? [f.aspect] : [];

  let tab: ReviewsInboxTab = 'open';
  if (f.reviewId || focusReviewId) tab = 'open';
  else if (f.responded === 'true') tab = 'answered';
  else if (f.responded === 'false') tab = 'open';
  else if (aspects.length > 0 || sentiments.length > 0) tab = 'all';

  return {
    tab,
    locationIds: f.locationId ? [f.locationId] : [],
    aspects,
    sentiments,
  };
}
